import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Mail, FileText, Users, Send, Plus, Trash2, Eye, Calendar, RefreshCw, Settings, Download, History, CheckCircle, XCircle, Clock, BarChart3, MousePointerClick, MailOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BulkEmail() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [bulkConfig, setBulkConfig] = useState(null);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', body: '' });
  
  // Senders
  const [senders, setSenders] = useState([]);
  const [showSenderDialog, setShowSenderDialog] = useState(false);
  const [senderForm, setSenderForm] = useState({ email: '', name: '' });
  
  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [campaignForm, setCampaignForm] = useState({ 
    name: '', 
    template_id: '', 
    sender_id: '', 
    dates: [],  // Multiple dates
    date_from: '',  // Date range start
    date_to: '',    // Date range end
    description: '' 
  });
  const [dateSelectionMode, setDateSelectionMode] = useState('single'); // 'single', 'multiple', 'range'
  
  // CRM Data
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDateCustomers, setSelectedDateCustomers] = useState(null);
  
  // Preview
  const [previewData, setPreviewData] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  
  // History
  const [history, setHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState('all');
  
  // Send Progress
  const [sendingCampaign, setSendingCampaign] = useState(null);
  const [sendProgress, setSendProgress] = useState({ sent: 0, failed: 0, total: 0, progress: 0 });
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  
  // Campaign Stats Dialog
  const [campaignStats, setCampaignStats] = useState(null);
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const [recipientDetails, setRecipientDetails] = useState([]);
  const [recipientFilter, setRecipientFilter] = useState('all');

  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const fetchBulkConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bulk/config`, { headers });
      if (res.ok) {
        setBulkConfig(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch bulk config:', error);
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bulk/stats`, { headers });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bulk/templates`, { headers });
      if (res.ok) {
        setTemplates(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  }, [token]);

  const fetchSenders = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bulk/senders`, { headers });
      if (res.ok) {
        setSenders(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch senders:', error);
    }
  }, [token]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bulk/campaigns`, { headers });
      if (res.ok) {
        setCampaigns(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  }, [token]);

  const fetchAvailableDates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/bulk/crm/dates`, { headers });
      if (res.ok) {
        setAvailableDates(await res.json());
      } else {
        toast.error('Failed to fetch CRM dates');
      }
    } catch (error) {
      toast.error('Failed to connect to CRM');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchHistory = useCallback(async () => {
    try {
      const statusParam = historyFilter !== 'all' ? `?status=${historyFilter}` : '';
      const res = await fetch(`${API_URL}/api/bulk/history${statusParam}`, { headers });
      if (res.ok) {
        setHistory(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  }, [token, historyFilter]);

  useEffect(() => {
    fetchBulkConfig();
    fetchStats();
    fetchTemplates();
    fetchSenders();
    fetchCampaigns();
    fetchAvailableDates();
    fetchHistory();
  }, [fetchBulkConfig, fetchStats, fetchTemplates, fetchSenders, fetchCampaigns, fetchAvailableDates, fetchHistory]);

  // Export campaign to Excel
  const handleExportCampaign = async (campaignId, campaignName) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/bulk/campaigns/${campaignId}/export`, { headers });
      if (res.ok) {
        const data = await res.json();
        
        // Create CSV content
        const csvHeaders = ['Timestamp', 'Email', 'First Name', 'Last Name', 'Full Name', 'Phone', 'State', 'Status', 'Error', 'Subject', 'Template Name', 'Campaign Name', 'Campaign Date', 'Sender Email'];
        const csvRows = data.export_data.map(row => [
          row.timestamp,
          row.email,
          row.first_name,
          row.last_name,
          row.full_name,
          row.phone,
          row.state,
          row.status,
          row.error || '',
          row.subject,
          row.template_name,
          row.campaign_name,
          row.campaign_date,
          row.sender_email
        ].map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(','));
        
        const csvContent = [csvHeaders.join(','), ...csvRows].join('\n');
        
        // Download as file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `bulk_campaign_${campaignName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(`Exported ${data.total_recipients} records`);
      } else {
        toast.error('Failed to export campaign');
      }
    } catch (error) {
      toast.error('Failed to export campaign');
    } finally {
      setLoading(false);
    }
  };

  // Template functions
  const handleSaveTemplate = async () => {
    try {
      setLoading(true);
      const url = editingTemplate 
        ? `${API_URL}/api/bulk/templates/${editingTemplate.id}`
        : `${API_URL}/api/bulk/templates`;
      const method = editingTemplate ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(templateForm)
      });
      
      if (res.ok) {
        toast.success(editingTemplate ? 'Template updated' : 'Template created');
        setShowTemplateDialog(false);
        setEditingTemplate(null);
        setTemplateForm({ name: '', subject: '', body: '' });
        fetchTemplates();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      const res = await fetch(`${API_URL}/api/bulk/templates/${id}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  // Sender functions
  const handleSaveSender = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/bulk/senders`, {
        method: 'POST',
        headers,
        body: JSON.stringify(senderForm)
      });
      
      if (res.ok) {
        toast.success('Sender added');
        setShowSenderDialog(false);
        setSenderForm({ email: '', name: '' });
        fetchSenders();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add sender');
      }
    } catch (error) {
      toast.error('Failed to add sender');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSender = async (id) => {
    if (!window.confirm('Delete this sender?')) return;
    try {
      const res = await fetch(`${API_URL}/api/bulk/senders/${id}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Sender deleted');
        fetchSenders();
      }
    } catch (error) {
      toast.error('Failed to delete sender');
    }
  };

  // Campaign functions
  const handleDateSelect = async (date, mode = 'single') => {
    if (mode === 'single') {
      // Single date - replace
      setCampaignForm(prev => ({ ...prev, dates: [date], date_from: '', date_to: '' }));
      try {
        const res = await fetch(`${API_URL}/api/bulk/crm/customers?date=${date}`, { headers });
        if (res.ok) {
          setSelectedDateCustomers(await res.json());
        }
      } catch (error) {
        toast.error('Failed to fetch customers');
      }
    } else if (mode === 'multiple') {
      // Multiple dates - toggle selection
      setCampaignForm(prev => {
        const currentDates = prev.dates || [];
        const newDates = currentDates.includes(date)
          ? currentDates.filter(d => d !== date)
          : [...currentDates, date];
        return { ...prev, dates: newDates, date_from: '', date_to: '' };
      });
    }
  };

  const handleDateRangeSelect = async (fromDate, toDate) => {
    setCampaignForm(prev => ({ ...prev, dates: [], date_from: fromDate, date_to: toDate }));
    try {
      const res = await fetch(`${API_URL}/api/bulk/crm/customers?date_from=${fromDate}&date_to=${toDate}`, { headers });
      if (res.ok) {
        setSelectedDateCustomers(await res.json());
      }
    } catch (error) {
      toast.error('Failed to fetch customers');
    }
  };

  const fetchCustomersForSelectedDates = async () => {
    const { dates, date_from, date_to } = campaignForm;
    try {
      let url = `${API_URL}/api/bulk/crm/customers?`;
      if (dates && dates.length > 0) {
        url += `dates=${dates.join(',')}`;
      } else if (date_from && date_to) {
        url += `date_from=${date_from}&date_to=${date_to}`;
      } else {
        return;
      }
      const res = await fetch(url, { headers });
      if (res.ok) {
        setSelectedDateCustomers(await res.json());
      }
    } catch (error) {
      toast.error('Failed to fetch customers');
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignForm.name || !campaignForm.template_id || !campaignForm.sender_id) {
      toast.error('Please fill all required fields');
      return;
    }
    
    // Validate date selection
    const hasDates = campaignForm.dates && campaignForm.dates.length > 0;
    const hasDateRange = campaignForm.date_from && campaignForm.date_to;
    
    if (!hasDates && !hasDateRange) {
      toast.error('Please select at least one date');
      return;
    }
    
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/bulk/campaigns`, {
        method: 'POST',
        headers,
        body: JSON.stringify(campaignForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Campaign created with ${data.total_recipients} recipients`);
        setShowCampaignDialog(false);
        setCampaignForm({ name: '', template_id: '', sender_id: '', dates: [], date_from: '', date_to: '', description: '' });
        setSelectedDateCustomers(null);
        setDateSelectionMode('single');
        setSelectedDateCustomers(null);
        fetchCampaigns();
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create campaign');
      }
    } catch (error) {
      toast.error('Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewCampaign = async (campaignId) => {
    try {
      const res = await fetch(`${API_URL}/api/bulk/campaigns/${campaignId}/preview`, { headers });
      if (res.ok) {
        setPreviewData(await res.json());
        setShowPreviewDialog(true);
      }
    } catch (error) {
      toast.error('Failed to load preview');
    }
  };

  const handleSendCampaign = async (campaignId) => {
    // Find campaign to get total
    const campaign = campaigns.find(c => c.id === campaignId);
    if (!campaign) return;
    
    if (!window.confirm(`Send this campaign to ${campaign.total_recipients - campaign.sent_count} pending recipients?`)) return;
    
    setSendingCampaign(campaignId);
    setSendProgress({ sent: 0, failed: 0, total: campaign.total_recipients - campaign.sent_count, progress: 0 });
    setShowProgressDialog(true);
    
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/bulk/campaigns/${campaignId}/send`, {
        method: 'POST',
        headers
      });
      
      if (res.ok) {
        const data = await res.json();
        setSendProgress({ 
          sent: data.sent, 
          failed: data.failed, 
          total: data.sent + data.failed, 
          progress: 100,
          remaining: data.remaining_pending,
          limitRemaining: data.daily_limit_remaining
        });
        toast.success(`Sent: ${data.sent}, Failed: ${data.failed}`);
        fetchCampaigns();
        fetchStats();
        fetchHistory();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send campaign');
        setShowProgressDialog(false);
      }
    } catch (error) {
      toast.error('Failed to send campaign');
      setShowProgressDialog(false);
    } finally {
      setLoading(false);
      setSendingCampaign(null);
    }
  };
  
  const handleViewStats = async (campaignId) => {
    try {
      setLoading(true);
      const [statsRes, recipientsRes] = await Promise.all([
        fetch(`${API_URL}/api/bulk/campaigns/${campaignId}/stats`, { headers }),
        fetch(`${API_URL}/api/bulk/campaigns/${campaignId}/recipients/detailed`, { headers })
      ]);
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setCampaignStats(data);
      }
      
      if (recipientsRes.ok) {
        const data = await recipientsRes.json();
        setRecipientDetails(data.recipients || []);
      }
      
      setRecipientFilter('all');
      setShowStatsDialog(true);
    } catch (error) {
      toast.error('Failed to load campaign stats');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRecipientsByFilter = async (campaignId, filter) => {
    try {
      let url = `${API_URL}/api/bulk/campaigns/${campaignId}/recipients/detailed`;
      if (filter !== 'all') {
        url += `?status_filter=${filter}`;
      }
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setRecipientDetails(data.recipients || []);
      }
    } catch (error) {
      console.error('Failed to filter recipients');
    }
  };

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      const res = await fetch(`${API_URL}/api/bulk/campaigns/${id}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Campaign deleted');
        fetchCampaigns();
      }
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-500',
      sending: 'bg-blue-500',
      completed: 'bg-green-500'
    };
    return <Badge className={`${colors[status] || 'bg-gray-500'} text-white`}>{status}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="bulk-email-page">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Today&apos;s Limit</p>
                <p className="text-2xl font-bold">{stats?.sent_today || 0} / {stats?.daily_limit || 150}</p>
              </div>
              <Mail className="h-8 w-8 text-[#0A2540]" />
            </div>
            <Progress value={(stats?.sent_today || 0) / (stats?.daily_limit || 150) * 100} className="mt-2" />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Remaining Today</p>
                <p className="text-2xl font-bold text-green-600">{stats?.remaining_today || 150}</p>
              </div>
              <Send className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Campaigns</p>
                <p className="text-2xl font-bold">{stats?.total_campaigns || 0}</p>
              </div>
              <FileText className="h-8 w-8 text-[#0A2540]" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Sent</p>
                <p className="text-2xl font-bold text-blue-600">{stats?.total_sent || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="campaigns" className="space-y-4">
        <TabsList>
          <TabsTrigger value="campaigns" data-testid="campaigns-tab">Campaigns</TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">History</TabsTrigger>
          <TabsTrigger value="templates" data-testid="templates-tab">Templates</TabsTrigger>
          <TabsTrigger value="senders" data-testid="senders-tab">Senders</TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Bulk Email Campaigns</h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchAvailableDates} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh CRM
              </Button>
              <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="new-campaign-btn" className="bg-[#0A2540]">
                    <Plus className="h-4 w-4 mr-2" />
                    New Campaign
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Bulk Campaign</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Campaign Name *</Label>
                      <Input
                        value={campaignForm.name}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., July Promotions"
                        data-testid="campaign-name-input"
                      />
                    </div>
                    
                    {/* Date Selection Mode */}
                    <div>
                      <Label>Date Selection Mode *</Label>
                      <div className="flex gap-2 mt-2">
                        <Button
                          type="button"
                          variant={dateSelectionMode === 'single' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            setDateSelectionMode('single');
                            setCampaignForm(prev => ({ ...prev, dates: [], date_from: '', date_to: '' }));
                            setSelectedDateCustomers(null);
                          }}
                          className={dateSelectionMode === 'single' ? 'bg-[#0A2540]' : ''}
                        >
                          Single Date
                        </Button>
                        <Button
                          type="button"
                          variant={dateSelectionMode === 'multiple' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            setDateSelectionMode('multiple');
                            setCampaignForm(prev => ({ ...prev, dates: [], date_from: '', date_to: '' }));
                            setSelectedDateCustomers(null);
                          }}
                          className={dateSelectionMode === 'multiple' ? 'bg-[#0A2540]' : ''}
                        >
                          Multiple Dates
                        </Button>
                        <Button
                          type="button"
                          variant={dateSelectionMode === 'range' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            setDateSelectionMode('range');
                            setCampaignForm(prev => ({ ...prev, dates: [], date_from: '', date_to: '' }));
                            setSelectedDateCustomers(null);
                          }}
                          className={dateSelectionMode === 'range' ? 'bg-[#0A2540]' : ''}
                        >
                          Date Range
                        </Button>
                      </div>
                    </div>
                    
                    {/* Single Date Selection */}
                    {dateSelectionMode === 'single' && (
                      <div>
                        <Label>Select Date (CRM) *</Label>
                        <Select 
                          value={campaignForm.dates[0] || ''} 
                          onValueChange={(date) => handleDateSelect(date, 'single')}
                        >
                          <SelectTrigger data-testid="campaign-date-select">
                            <SelectValue placeholder="Select date from CRM" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableDates.map(d => (
                              <SelectItem key={d.date} value={d.date}>
                                {d.date} ({d.count} customers)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {/* Multiple Dates Selection */}
                    {dateSelectionMode === 'multiple' && (
                      <div>
                        <Label>Select Multiple Dates (click to toggle) *</Label>
                        <div className="border rounded-lg p-3 mt-2 max-h-48 overflow-y-auto">
                          <div className="flex flex-wrap gap-2">
                            {availableDates.map(d => (
                              <Button
                                key={d.date}
                                type="button"
                                variant={campaignForm.dates.includes(d.date) ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handleDateSelect(d.date, 'multiple')}
                                className={campaignForm.dates.includes(d.date) ? 'bg-green-600 hover:bg-green-700' : ''}
                              >
                                {d.date} ({d.count})
                              </Button>
                            ))}
                          </div>
                        </div>
                        {campaignForm.dates.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm text-blue-600">
                              Selected: {campaignForm.dates.length} date(s)
                            </p>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="mt-1"
                              onClick={fetchCustomersForSelectedDates}
                            >
                              Fetch Customers for Selected Dates
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Date Range Selection */}
                    {dateSelectionMode === 'range' && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>From Date *</Label>
                            <Select 
                              value={campaignForm.date_from} 
                              onValueChange={(date) => {
                                setCampaignForm(prev => ({ ...prev, date_from: date }));
                                if (campaignForm.date_to) {
                                  handleDateRangeSelect(date, campaignForm.date_to);
                                }
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Start date" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableDates.map(d => (
                                  <SelectItem key={d.date} value={d.date}>
                                    {d.date}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>To Date *</Label>
                            <Select 
                              value={campaignForm.date_to} 
                              onValueChange={(date) => {
                                setCampaignForm(prev => ({ ...prev, date_to: date }));
                                if (campaignForm.date_from) {
                                  handleDateRangeSelect(campaignForm.date_from, date);
                                }
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="End date" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableDates.map(d => (
                                  <SelectItem key={d.date} value={d.date}>
                                    {d.date}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Customer Count */}
                    {selectedDateCustomers && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-700 font-medium">
                          ✓ {selectedDateCustomers.total} unique customers found
                          {selectedDateCustomers.dates && selectedDateCustomers.dates.length > 1 && (
                            <span className="text-green-600 ml-1">
                              (across {selectedDateCustomers.dates.length} dates)
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <Label>Template *</Label>
                      <Select 
                        value={campaignForm.template_id} 
                        onValueChange={(v) => setCampaignForm(prev => ({ ...prev, template_id: v }))}
                      >
                        <SelectTrigger data-testid="campaign-template-select">
                          <SelectValue placeholder="Select template" />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Sender *</Label>
                      <Select 
                        value={campaignForm.sender_id} 
                        onValueChange={(v) => setCampaignForm(prev => ({ ...prev, sender_id: v }))}
                      >
                        <SelectTrigger data-testid="campaign-sender-select">
                          <SelectValue placeholder="Select sender" />
                        </SelectTrigger>
                        <SelectContent>
                          {senders.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Description (Optional)</Label>
                      <Textarea
                        value={campaignForm.description}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Campaign notes..."
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCampaignDialog(false)}>Cancel</Button>
                    <Button 
                      onClick={handleCreateCampaign} 
                      disabled={loading}
                      className="bg-[#0A2540]"
                      data-testid="create-campaign-btn"
                    >
                      Create Campaign
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="grid gap-4">
            {campaigns.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  No campaigns yet. Create your first bulk email campaign.
                </CardContent>
              </Card>
            ) : (
              campaigns.map(campaign => (
                <Card key={campaign.id} data-testid={`campaign-${campaign.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{campaign.name}</h3>
                          {getStatusBadge(campaign.status)}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Date: {campaign.date} | Template: {campaign.template_name}
                        </p>
                        <p className="text-sm text-gray-500">
                          Sender: {campaign.sender_email}
                        </p>
                        <div className="flex gap-4 mt-2 text-sm">
                          <span>Total: <strong>{campaign.total_recipients}</strong></span>
                          <span className="text-green-600">Sent: <strong>{campaign.sent_count}</strong></span>
                          <span className="text-red-600">Failed: <strong>{campaign.failed_count}</strong></span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {campaign.status === 'completed' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleViewStats(campaign.id)}
                            title="View Stats"
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            <BarChart3 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handlePreviewCampaign(campaign.id)}
                          title="Preview"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleExportCampaign(campaign.id, campaign.name)}
                          disabled={loading}
                          title="Download Excel"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {campaign.status !== 'completed' && (
                          <Button 
                            size="sm"
                            onClick={() => handleSendCampaign(campaign.id)}
                            disabled={loading || sendingCampaign === campaign.id}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Send className="h-4 w-4 mr-1" />
                            {sendingCampaign === campaign.id ? 'Sending...' : 'Send'}
                          </Button>
                        )}
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => handleDeleteCampaign(campaign.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Bulk Email History</h2>
            <div className="flex gap-2">
              <Select value={historyFilter} onValueChange={(v) => setHistoryFilter(v)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchHistory}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Timestamp</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Recipient</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Template</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Sender</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                          No bulk email history yet
                        </td>
                      </tr>
                    ) : (
                      history.map((log, idx) => (
                        <tr key={log.id || idx} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-600">
                            {new Date(log.sent_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">{log.recipient_email}</td>
                          <td className="px-4 py-3">{log.template_name}</td>
                          <td className="px-4 py-3 text-gray-500">{log.sender_email}</td>
                          <td className="px-4 py-3">
                            {log.status === 'sent' ? (
                              <Badge className="bg-green-600">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Sent
                              </Badge>
                            ) : log.status === 'failed' ? (
                              <Badge variant="destructive">
                                <XCircle className="h-3 w-3 mr-1" />
                                Failed
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                <Clock className="h-3 w-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Bulk Email Templates</h2>
            <Dialog open={showTemplateDialog} onOpenChange={(open) => {
              setShowTemplateDialog(open);
              if (!open) {
                setEditingTemplate(null);
                setTemplateForm({ name: '', subject: '', body: '' });
              }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="new-template-btn" className="bg-[#0A2540]">
                  <Plus className="h-4 w-4 mr-2" />
                  New Template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Template Name</Label>
                    <Input
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Welcome Email"
                      data-testid="template-name-input"
                    />
                  </div>
                  <div>
                    <Label>Subject</Label>
                    <Input
                      value={templateForm.subject}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="e.g., Hello {{first_name}}!"
                      data-testid="template-subject-input"
                    />
                  </div>
                  <div>
                    <Label>Body (HTML)</Label>
                    <Textarea
                      value={templateForm.body}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, body: e.target.value }))}
                      placeholder="Use {{first_name}}, {{last_name}}, {{email}}, {{phone}}, {{state}} as variables"
                      rows={10}
                      data-testid="template-body-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Available variables: {`{{first_name}}, {{last_name}}, {{customer_name}}, {{email}}, {{phone}}, {{state}}`}
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
                  <Button 
                    onClick={handleSaveTemplate} 
                    disabled={loading}
                    className="bg-[#0A2540]"
                    data-testid="save-template-btn"
                  >
                    {editingTemplate ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {templates.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  No templates yet. Create your first bulk email template.
                </CardContent>
              </Card>
            ) : (
              templates.map(template => (
                <Card key={template.id} data-testid={`template-${template.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">{template.name}</h3>
                        <p className="text-sm text-gray-500">Subject: {template.subject}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Variables: {template.variables?.join(', ') || 'None'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setEditingTemplate(template);
                            setTemplateForm({
                              name: template.name,
                              subject: template.subject,
                              body: template.body
                            });
                            setShowTemplateDialog(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => handleDeleteTemplate(template.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Senders Tab */}
        <TabsContent value="senders" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">Bulk Email Senders</h2>
              {bulkConfig && (
                <p className="text-sm text-gray-500">
                  Provider: <Badge variant="outline">{bulkConfig.provider}</Badge> | 
                  Domains: {bulkConfig.domains?.map((d, i) => (
                    <Badge key={d} className="bg-blue-600 ml-1">@{d}</Badge>
                  ))}
                </p>
              )}
            </div>
            <Dialog open={showSenderDialog} onOpenChange={setShowSenderDialog}>
              <DialogTrigger asChild>
                <Button data-testid="new-sender-btn" className="bg-[#0A2540]">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Sender
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Bulk Sender</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      value={senderForm.email}
                      onChange={(e) => setSenderForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder={bulkConfig?.domains?.[0] ? `e.g., promo@${bulkConfig.domains[0]}` : "e.g., promo@domain.com"}
                      data-testid="sender-email-input"
                    />
                    {bulkConfig?.domains && (
                      <p className="text-xs text-blue-600 mt-1">
                        Must use one of: {bulkConfig.domains.map(d => `@${d}`).join(', ')} (Resend verified)
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Display Name</Label>
                    <Input
                      value={senderForm.name}
                      onChange={(e) => setSenderForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Connect My Offer Promotions"
                      data-testid="sender-name-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowSenderDialog(false)}>Cancel</Button>
                  <Button 
                    onClick={handleSaveSender} 
                    disabled={loading}
                    className="bg-[#0A2540]"
                    data-testid="save-sender-btn"
                  >
                    Add Sender
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {senders.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  No senders yet. Add a sender email for bulk campaigns.
                </CardContent>
              </Card>
            ) : (
              senders.map(sender => (
                <Card key={sender.id} data-testid={`sender-${sender.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-semibold">{sender.name}</h3>
                        <p className="text-sm text-gray-500">{sender.email}</p>
                      </div>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleDeleteSender(sender.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Campaign Preview</DialogTitle>
          </DialogHeader>
          {previewData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><strong>Campaign:</strong> {previewData.campaign?.name}</div>
                <div><strong>Date:</strong> {previewData.campaign?.date}</div>
                <div><strong>Template:</strong> {previewData.template?.name}</div>
                <div><strong>Pending:</strong> {previewData.total_pending}</div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Sample Email Previews (First 5)</h4>
                {previewData.sample_previews?.map((preview, i) => (
                  <Card key={i} className="mb-2">
                    <CardContent className="p-3">
                      <p className="text-sm"><strong>To:</strong> {preview.recipient}</p>
                      <p className="text-sm"><strong>Subject:</strong> {preview.subject}</p>
                      <div className="text-xs text-gray-500 mt-2 p-2 bg-gray-50 rounded">
                        <div dangerouslySetInnerHTML={{ __html: preview.body_preview }} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Progress Dialog */}
      <Dialog open={showProgressDialog} onOpenChange={setShowProgressDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Sending Campaign
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#0A2540] mb-2">
                {sendProgress.sent + sendProgress.failed} / {sendProgress.total}
              </div>
              <p className="text-sm text-gray-500">Emails Processed</p>
            </div>
            
            <Progress value={sendProgress.progress} className="h-3" />
            
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="p-3 bg-green-50 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-1" />
                <div className="text-2xl font-bold text-green-600">{sendProgress.sent}</div>
                <p className="text-xs text-green-700">Sent</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <XCircle className="h-6 w-6 text-red-600 mx-auto mb-1" />
                <div className="text-2xl font-bold text-red-600">{sendProgress.failed}</div>
                <p className="text-xs text-red-700">Failed</p>
              </div>
            </div>
            
            {sendProgress.progress === 100 && (
              <div className="text-center text-sm text-gray-500 border-t pt-3">
                {sendProgress.remaining > 0 ? (
                  <p>Remaining pending: {sendProgress.remaining} (hit daily limit)</p>
                ) : (
                  <p className="text-green-600 font-medium">All emails sent successfully!</p>
                )}
                <p className="mt-1">Daily limit remaining: {sendProgress.limitRemaining}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              onClick={() => setShowProgressDialog(false)} 
              disabled={sendProgress.progress < 100}
              className="w-full"
            >
              {sendProgress.progress < 100 ? 'Sending...' : 'Done'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Stats Dialog */}
      <Dialog open={showStatsDialog} onOpenChange={setShowStatsDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Campaign Performance: {campaignStats?.campaign_name}
            </DialogTitle>
          </DialogHeader>
          
          {campaignStats && (
            <div className="space-y-6">
              {/* Stats Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <Mail className="h-6 w-6 text-blue-600 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-blue-700">{campaignStats.sent}</div>
                    <p className="text-xs text-blue-600">Delivered</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4 text-center">
                    <MailOpen className="h-6 w-6 text-green-600 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-green-700">{campaignStats.opened}</div>
                    <p className="text-xs text-green-600">Opened ({campaignStats.open_rate}%)</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4 text-center">
                    <MousePointerClick className="h-6 w-6 text-purple-600 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-purple-700">{campaignStats.clicked}</div>
                    <p className="text-xs text-purple-600">Clicked ({campaignStats.click_rate}%)</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4 text-center">
                    <XCircle className="h-6 w-6 text-red-600 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-red-700">{campaignStats.failed}</div>
                    <p className="text-xs text-red-600">Failed</p>
                  </CardContent>
                </Card>
              </div>

              {/* Visual Progress Bars */}
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Open Rate</span>
                    <span className="font-medium">{campaignStats.open_rate}%</span>
                  </div>
                  <Progress value={campaignStats.open_rate} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Click Rate</span>
                    <span className="font-medium">{campaignStats.click_rate}%</span>
                  </div>
                  <Progress value={campaignStats.click_rate} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Click-to-Open Rate</span>
                    <span className="font-medium">{campaignStats.click_to_open_rate}%</span>
                  </div>
                  <Progress value={campaignStats.click_to_open_rate} className="h-2" />
                </div>
              </div>

              {/* Recipient Details */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold">Recipient Details</h4>
                  <Select 
                    value={recipientFilter} 
                    onValueChange={(v) => {
                      setRecipientFilter(v);
                      fetchRecipientsByFilter(campaignStats.campaign_id, v);
                    }}
                  >
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Recipients</SelectItem>
                      <SelectItem value="opened">Opened</SelectItem>
                      <SelectItem value="not_opened">Not Opened</SelectItem>
                      <SelectItem value="clicked">Clicked</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="max-h-[300px] overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Email</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-600">Status</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-600">Opened</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-600">Clicks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipientDetails.map((r, i) => (
                        <tr key={r.id || i} className="border-t hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-xs">{r.email}</td>
                          <td className="px-3 py-2">{r.first_name} {r.last_name}</td>
                          <td className="px-3 py-2 text-center">
                            {r.status === 'sent' ? (
                              <Badge className="bg-green-500 text-white text-xs">Sent</Badge>
                            ) : r.status === 'failed' ? (
                              <Badge className="bg-red-500 text-white text-xs">Failed</Badge>
                            ) : (
                              <Badge className="bg-gray-500 text-white text-xs">{r.status}</Badge>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {r.opened ? (
                              <span className="text-green-600" title={`Opened: ${r.opened_at}`}>
                                <MailOpen className="h-4 w-4 inline" />
                              </span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {r.click_count > 0 ? (
                              <span className="text-purple-600 font-medium">{r.click_count}</span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {recipientDetails.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                            No recipients found with selected filter
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
